<template>
  <v-col>
    <v-row no-gutters>
      <!-- LEFT SIDE: 3 columns total (contains its own grid) -->
      <v-col cols="3">
        <v-row no-gutters>
          <!-- Keep the 1:2 ratio inside the nested grid: 4 + 8 = 12 -->
          <v-col
            cols="4"
            style="margin-top: 48px; border-radius: 20px;"
          >
            <div class="deposits-cell">
              {{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TimeDeposits') }}
              <span class="synthesis-ui-grey-01--text">{{ formatNumber(DepositBehavior.timeDepositsAmount) }}</span>
            </div>
            <div class="deposits-cell">
              {{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.SavingsDeposits') }}
              <span class="synthesis-ui-grey-01--text">{{ formatNumber(DepositBehavior.savingsDepositsAmount) }}</span>
            </div>
            <div class="deposits-cell">
              <p>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.DemandDeposits') }}</p>
              <span class="synthesis-ui-grey-01--text">{{ formatNumber(DepositBehavior.demandDepositsAmount) }}</span>
            </div>
          </v-col>

          <v-col
            cols="8"
            style="margin-top: 48px; border-radius: 20px;"
          >
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Insured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.timeDepositsInsuredAmount) }}
              </div>
            </div>
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Uninsured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.timeDepositsUninsuredAmount) }}
              </div>
            </div>
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Insured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.savingsDepositsInsuredAmount) }}
              </div>
            </div>
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Uninsured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.savingsDepositsUninsuredAmount) }}
              </div>
            </div>
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Insured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.demandDepositsInsuredAmount) }}
              </div>
            </div>
            <div class="amount-cell">
              <div>{{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.Uninsured') }}</div>
              <div class="amount-cell-value">
                {{ formatNumber(DepositBehavior.demandDepositsUninsuredAmount) }}
              </div>
            </div>
          </v-col>

          <!-- YOUR LAST ROW under the first two columns ONLY -->
          <v-col cols="12">
            <div class="bottom-row pa-4 d-flex justify-center font-weight-bold text-size-12">
              {{ $t('RiskLiquidity.Section.DefenceReadiness.MildCrisisAssumptions.TotalOutflow') }}
            </div>
          </v-col>
        </v-row>
      </v-col>

      <!-- RIGHT SIDE: data table taking 9 columns, can span multiple visual rows -->
      <v-col
        cols="9"
        style="border-radius: 10px;"
      >
        <table-header-gradient-blue>
          <template #table>
            <v-data-table
              :headers="tableHeaders"
              :items="flattenedTableData"
              class="mild-crisis-table font-weight-medium synthesis-card-border"
              disable-pagination
              disable-sort
              hide-default-footer
              item-key="id"
            >
              <template
                v-for="(colHeader) in tableHeaders"
                #[`item.${colHeader.value}`]="{item , index}"
              >
                <div :key="`${item.id}-${colHeader.value}`">
                  <div v-if="editMode && (index !== flattenedTableData.length - 1)">
                    <v-text-field
                      :value="removePercentage(getCellValue(item, colHeader.value))"
                      compact
                      dense
                      hide-details
                      outlined
                      type="number"
                      hide-spin-buttons
                      class="edit-cell-field centered-input"
                      :rules="[v => !!v || 'Required']"
                      @input="updateCellValue(item.id, index, colHeader.value, $event)"
                    />
                  </div>
                  <span
                    v-else
                    class="synthesis-ui-grey-01--text"
                  >{{ getCellValue(item, colHeader.value) }}</span>
                </div>
              </template>
            </v-data-table>
          </template>
        </table-header-gradient-blue>
      </v-col>
    </v-row>
  </v-col>
</template>

    <script>
import TableHeaderGradientBlue from '@/theme/default/components/common/TableHeaderGradientBlue'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'

export default {
  name      : 'DepositorBehaviorTableView',
  components: {
    TableHeaderGradientBlue
  },
  mixins: [riskBaseCurrency],
  props : {
    editMode: {
      type   : Boolean,
      default: false
    },
    tableData: {
      type   : Object,
      default: () => {}
    }
  },
  dataStore: {
    DepositBehavior     : 'RiskLiquidity.DepositBehavior.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data',
    assumptionData      : 'RiskLiquidity.DepositBehavior.Assumptions'
  },
  data () {
    return {
      currentPage   : 1,
      editableValues: {},
      assumptionKeys: ['timeDepositsInsuredWithdrawalPercentages',
                       'timeDepositsUninsuredWithdrawalPercentages',
                       'savingsDepositsInsuredWithdrawalPercentages',
                       'savingsDepositsUninsuredWithdrawalPercentages',
                       'demandDepositsInsuredWithdrawalPercentages',
                       'demandDepositsUninsuredWithdrawalPercentages']
    }
  },
  computed: {
    id () {
      return this.user?.selectedAccountId || null
    },
    tableHeaders () {
      const tableHeaders = this.DepositBehavior.items.map(item => {
        return {
          text : `Day ${item.day}`,
          value: `d${item.day}`,
          width: '130px',
          class: 'grey lighten-5',
          align: 'center'
        }
      })

      return tableHeaders
    },

    flattenedTableData () {
      // iterate over object keys and the values for the totalOutflow
      const totalOutflow = {}
      Object.keys(this.tableData.totalOutflow).forEach(key => {
        totalOutflow[key] = `${this.formatNumber(this.tableData.totalOutflow[key])}`
      })

      const tableData = [this.tableData.timeDepositsInsured,
                         this.tableData.timeDepositsUninsured,
                         this.tableData.savingsDepositsInsured,
                         this.tableData.savingsDepositsUninsured,
                         this.tableData.demandDepositsInsured,
                         this.tableData.demandDepositsUninsured].map(item => item.percentage)

      this.fixEditableValues(...[this.tableData.timeDepositsInsured,
                                 this.tableData.timeDepositsUninsured,
                                 this.tableData.savingsDepositsInsured,
                                 this.tableData.savingsDepositsUninsured,
                                 this.tableData.demandDepositsInsured,
                                 this.tableData.demandDepositsUninsured].map(item => item.percentage))

      // flatten the object into an array
      return [...tableData, totalOutflow]
    }

  },
  watch: {
    editMode: {
      handler (newVal) {
        // editMode changed
      }
    }
  },

  methods: {

    formatNumber (value, includeCurrency = true) {
      return includeCurrency ? this.riskBaseCurrencyDisplayName + toNumberFormatNotationShort(value) : toNumberFormatNotationShort(value)
    },
    getCellValue (item, value) {
      return item[value]
    },
    updateCellValue (id, rowIndex, itemIndex, newValue) {
      const key = this.getKeyFromIndex(rowIndex)

      if (this.editableValues[key]) {
        this.editableValues[key][itemIndex] = newValue
      }

      // Flatten the assumption data from day objects to arrays
      this.assumptionData = this.flattenAssumptionData(this.editableValues)
    },
    removePercentage (percentageString) {
      return parseFloat(percentageString.replace('%', ''))
    },
    fixEditableValues (timeDepositsInsuredWithdrawalPercentages, timeDepositsUninsuredWithdrawalPercentages, savingsDepositsInsuredWithdrawalPercentages, savingsDepositsUninsuredWithdrawalPercentages, demandDepositsInsuredWithdrawalPercentages, demandDepositsUninsuredWithdrawalPercentages) {
      this.editableValues = {
        [this.getKeyFromIndex(0)]: timeDepositsInsuredWithdrawalPercentages,
        [this.getKeyFromIndex(1)]: timeDepositsUninsuredWithdrawalPercentages,
        [this.getKeyFromIndex(2)]: savingsDepositsInsuredWithdrawalPercentages,
        [this.getKeyFromIndex(3)]: savingsDepositsUninsuredWithdrawalPercentages,
        [this.getKeyFromIndex(4)]: demandDepositsInsuredWithdrawalPercentages,
        [this.getKeyFromIndex(5)]: demandDepositsUninsuredWithdrawalPercentages
      }
    },
    getKeyFromIndex (index) {
      return this.assumptionKeys[index]
    },

    flattenAssumptionData (editableValues) {
      const flattened = {}

      // Iterate through each assumption key
      this.assumptionKeys.forEach(key => {
        if (editableValues[key]) {
          // Convert the day object to an array of values
          const dayValues = []

          // Extract values from d1, d2, d3, etc. and convert to numbers
          Object.keys(editableValues[key]).forEach(dayKey => {
            if (dayKey.startsWith('d')) {
              const value = editableValues[key][dayKey]
              // Remove percentage sign and convert to number
              const numericValue = this.removePercentage(value)
              dayValues.push(numericValue)
            }
          })

          flattened[key] = dayValues
        }
      })

      return flattened
    }
  }
}
    </script>

    <style scoped>
.amount-cell {
  height: 48px;
  background-color: var(--v-synthesis-ui-blue-07-base);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  padding: 0 16px;
  border-bottom: 1px solid var(--v-synthesis-ui-blue-08-base);
}
.deposits-cell {
  height: 96px;
  background-color: var(--v-synthesis-ui-blue-08-base);

  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  padding: 10px 16px;

  border-bottom: 1px solid var(--v-synthesis-ui-blue-08-base);
}
.amount-cell-value {
  color: var(--v-synthesis-ui-grey-01-base);
  background-color: rgba(244, 247, 255, 1);
  border-radius: 4px;
  padding: 4px 8px;
}

::v-deep .centered-input input {
    text-align: center;
}
    </style>
